import { CSSProperties } from 'react';

import { Theme } from '@mui/material';

import { polarToCartesian } from '@cast/utils';

import { PieChartData } from './types';
import { mapPaletteColorToDataColorToken } from '../../../../utils/mapPaletteColorToDataColorToken';
import { getThemeColor } from '../../../../utils';
import { DataKey } from '../../types';

const RADIAN = Math.PI / 180;
const DEG_PER_RAD = 1 / RADIAN;

/** Converts a pixel offset to an angular offset at a given radius. */
export const pxToAngle = (px: number, radius: number): number =>
  radius > 0 ? (px / radius) * DEG_PER_RAD : 0;

type EdgeTangentPointsParams = {
  a: { x: number; y: number };
  b: { x: number; y: number };
  distance: number;
};
type EdgeTangentPointsResult = {
  fromA: { x: number; y: number };
  fromB: { x: number; y: number };
};
/**
 * Returns two points along a line: one at `distance` from `a` toward `b`,
 * and one at `distance` from `b` toward `a`. Shares the sqrt cost.
 */
export const edgeTangentPoints = ({
  a,
  b,
  distance,
}: EdgeTangentPointsParams): EdgeTangentPointsResult => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return { fromA: a, fromB: b };
  const t = distance / len;
  return {
    fromA: { x: a.x + dx * t, y: a.y + dy * t },
    fromB: { x: b.x - dx * t, y: b.y - dy * t },
  };
};

type GetArcTangentParams = {
  cx: number;
  cy: number;
  radius: number;
  angle: number;
  sign: number;
  isExternal: boolean;
  cr: number;
};
type GetArcTangentResult = {
  pt: { x: number; y: number };
  theta: number;
};
/**
 * Compute the arc tangent point and angular offset for a corner.
 * Uses Recharts' tangent circle algorithm.
 *
 * isExternal=false for outer corners (circle inside the arc),
 * isExternal=true for inner corners (circle outside the arc).
 */
export const getArcTangent = ({
  cx,
  cy,
  radius,
  angle,
  sign,
  isExternal,
  cr,
}: GetArcTangentParams): GetArcTangentResult => {
  const centerRadius = cr * (isExternal ? 1 : -1) + radius;
  if (centerRadius <= 0 || cr > centerRadius) {
    return {
      pt: polarToCartesian({ cx, cy, radius, angle }),
      theta: 0,
    };
  }
  const theta = Math.asin(cr / centerRadius) * DEG_PER_RAD;
  return {
    pt: polarToCartesian({ cx, cy, radius, angle: angle + sign * theta }),
    theta,
  };
};

type SectorPathParams = {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  insetStartGap: number;
  insetEndGap: number;
  cornerRadius: number;
  sign: number;
};
/**
 * Builds an SVG path for a sector with:
 * - Per-radius angular insets for straight, uniform-width gap edges
 * - Corner radius using the same tangent-circle algorithm as Recharts
 */
export const buildSectorPath = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  insetStartGap,
  insetEndGap,
  cornerRadius,
  sign,
}: SectorPathParams): string => {
  const cr = Math.min(cornerRadius, (outerRadius - innerRadius) / 2);

  const spanRad = Math.abs(endAngle - startAngle) * RADIAN;
  const availableOuter = spanRad * outerRadius;
  const availableInner =
    innerRadius > 0 ? spanRad * innerRadius : Number.POSITIVE_INFINITY;
  const totalInset = insetStartGap + insetEndGap;
  const insetScale =
    totalInset > 0
      ? Math.min(1, availableOuter / totalInset, availableInner / totalInset)
      : 1;
  const startGap = insetStartGap * insetScale;
  const endGap = insetEndGap * insetScale;

  // Per-radius inset angles for straight gap edges
  const sO = startAngle + sign * pxToAngle(startGap, outerRadius);
  const sI = startAngle + sign * pxToAngle(startGap, innerRadius);
  const eO = endAngle - sign * pxToAngle(endGap, outerRadius);
  const eI = endAngle - sign * pxToAngle(endGap, innerRadius);

  const swOuter = sign < 0 ? 1 : 0;
  const swInner = sign < 0 ? 0 : 1;

  // The 4 corner points
  const pSO = polarToCartesian({ cx, cy, radius: outerRadius, angle: sO });
  const pEO = polarToCartesian({ cx, cy, radius: outerRadius, angle: eO });
  const pEI = polarToCartesian({ cx, cy, radius: innerRadius, angle: eI });
  const pSI = polarToCartesian({ cx, cy, radius: innerRadius, angle: sI });

  if (cr <= 0) {
    const laO = Math.abs(eO - sO) > 180 ? 1 : 0;
    const laI = Math.abs(eI - sI) > 180 ? 1 : 0;
    return `M ${pSO.x} ${pSO.y} A ${outerRadius} ${outerRadius} 0 ${laO} ${swOuter} ${pEO.x} ${pEO.y} L ${pEI.x} ${pEI.y} A ${innerRadius} ${innerRadius} 0 ${laI} ${swInner} ${pSI.x} ${pSI.y} Z`;
  }

  // Arc tangent points for the outer edge
  const so = getArcTangent({
    cx,
    cy,
    radius: outerRadius,
    angle: sO,
    sign,
    isExternal: false,
    cr,
  });
  const eo = getArcTangent({
    cx,
    cy,
    radius: outerRadius,
    angle: eO,
    sign: -sign,
    isExternal: false,
    cr,
  });

  const oArc = Math.abs(sO - eO) - so.theta - eo.theta;

  // When innerRadius === 0 the inner "arc" degenerates to the center point.
  // Skip inner tangent computation entirely — it always produces theta=90 which
  // incorrectly triggers the fallback for normal-sized sectors.
  if (innerRadius === 0) {
    if (oArc < 0) {
      return buildSectorPath({
        cx,
        cy,
        innerRadius,
        outerRadius,
        startAngle,
        endAngle,
        insetStartGap,
        insetEndGap,
        cornerRadius: 0,
        sign,
      });
    }

    const laO = oArc > 180 ? 1 : 0;
    // pEI and pSI both collapse to center — straight edges run from outer corners to (cx, cy).
    // Compute tangent points along those edges at distance cr from the outer corners.
    const startEdge = edgeTangentPoints({
      a: pSO,
      b: { x: cx, y: cy },
      distance: cr,
    });
    const endEdge = edgeTangentPoints({
      a: pEO,
      b: { x: cx, y: cy },
      distance: cr,
    });
    return `M ${startEdge.fromA.x} ${startEdge.fromA.y} A ${cr} ${cr} 0 0 ${swOuter} ${so.pt.x} ${so.pt.y} A ${outerRadius} ${outerRadius} 0 ${laO} ${swOuter} ${eo.pt.x} ${eo.pt.y} A ${cr} ${cr} 0 0 ${swOuter} ${endEdge.fromA.x} ${endEdge.fromA.y} L ${cx} ${cy} Z`;
  }

  const si = getArcTangent({
    cx,
    cy,
    radius: innerRadius,
    angle: sI,
    sign,
    isExternal: true,
    cr,
  });
  const ei = getArcTangent({
    cx,
    cy,
    radius: innerRadius,
    angle: eI,
    sign: -sign,
    isExternal: true,
    cr,
  });

  const iArc = Math.abs(sI - eI) - si.theta - ei.theta;

  // Fall back to no-corner if sector too small
  if (oArc < 0 || iArc < 0) {
    return buildSectorPath({
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      insetStartGap,
      insetEndGap,
      cornerRadius: 0,
      sign,
    });
  }

  // Line tangent points along the straight gap edges
  const startEdge = edgeTangentPoints({ a: pSO, b: pSI, distance: cr });
  const endEdge = edgeTangentPoints({ a: pEO, b: pEI, distance: cr });

  const laO = oArc > 180 ? 1 : 0;
  const laI = iArc > 180 ? 1 : 0;

  return `M ${startEdge.fromA.x} ${startEdge.fromA.y} A ${cr} ${cr} 0 0 ${swOuter} ${so.pt.x} ${so.pt.y} A ${outerRadius} ${outerRadius} 0 ${laO} ${swOuter} ${eo.pt.x} ${eo.pt.y} A ${cr} ${cr} 0 0 ${swOuter} ${endEdge.fromA.x} ${endEdge.fromA.y} L ${endEdge.fromB.x} ${endEdge.fromB.y} A ${cr} ${cr} 0 0 ${swOuter} ${ei.pt.x} ${ei.pt.y} A ${innerRadius} ${innerRadius} 0 ${laI} ${swInner} ${si.pt.x} ${si.pt.y} A ${cr} ${cr} 0 0 ${swOuter} ${startEdge.fromB.x} ${startEdge.fromB.y} Z`;
};

const MAX_SECTOR_WIDTH = 30;

export const calculateSectorWidth = (
  radius: number,
  userWidth?: number
): number => {
  const inset = 0.1;
  return userWidth ?? Math.min(radius / 2 - radius * inset, MAX_SECTOR_WIDTH);
};

export const modifyDataStyle = (
  theme: Theme,
  data?: PieChartData,
  suppressStrokeFallback?: boolean
): CSSProperties => {
  const _fill = data?.style?.fill ?? data?.fill;
  const _stroke =
    data?.style?.stroke ??
    data?.stroke ??
    (suppressStrokeFallback ? undefined : _fill);

  const fill = _fill
    ? getThemeColor(theme, mapPaletteColorToDataColorToken(_fill, _fill))
    : undefined;
  const stroke = _stroke
    ? getThemeColor(theme, mapPaletteColorToDataColorToken(_stroke, _stroke))
    : undefined;

  return {
    ...data?.style,
    fill,
    stroke,
  };
};

export const hasValue = <T extends PieChartData>(
  point: T,
  dataKey: DataKey<T> = 'value'
): boolean => {
  const value =
    typeof dataKey === 'function' ? dataKey(point) : point[dataKey as string];
  return !!value;
};

export const calculateInnerRadius = ({
  radius,
  sectorGap,
  sectorWidth,
}: {
  radius: number;
  sectorGap: number;
  sectorWidth?: number;
}): number | undefined => {
  return sectorWidth !== undefined
    ? radius - Math.max(0, Math.min(sectorWidth, radius - sectorGap / 2))
    : undefined;
};

export const getIsFullCircle = ({
  startAngle,
  endAngle,
}: {
  startAngle: number;
  endAngle: number;
}): boolean => {
  return Math.abs(startAngle - endAngle) >= 360;
};
